/**
 * Footage intake helpers: what we accept, the upload path a file takes, and the metadata the
 * upload flow records on FootageAsset (filename, duration, resolution, codec, size — plan §3.1).
 *
 * Two ceilings, deliberately:
 *   * `FOOTAGE_SINGLE_SHOT_BYTES` mirrors the worker's `MAX_UPLOAD_BYTES` — the most a single
 *     multipart POST can carry, because Cloudflare caps a Worker request body at 100 MB (free) /
 *     500 MB (paid).
 *   * `FOOTAGE_MAX_BYTES` mirrors the worker's `MAX_FOOTAGE_BYTES` — the real accepted ceiling.
 *     Above the single-shot limit the file is chunked through the R2 multipart flow
 *     (start → signed part URLs → complete), where the request-body cap no longer applies.
 */

export const VIDEO_EXTENSIONS = /\.(mp4|m4v|mov)$/i;

/** Keep in sync with workers/api/wrangler.toml `MAX_UPLOAD_BYTES`. */
export const FOOTAGE_SINGLE_SHOT_BYTES = 100 * 1024 * 1024;

/** Keep in sync with workers/api/wrangler.toml `MAX_FOOTAGE_BYTES`. */
export const FOOTAGE_MAX_BYTES = 4 * 1024 * 1024 * 1024;

export type FootageUploadPlan = { mode: 'single-shot' } | { mode: 'multipart' };

/**
 * Which upload path this file takes. Files at or under the Worker request-body ceiling keep the
 * proven single-shot multipart POST; larger files go through the R2 multipart flow, where each
 * request carries one part and the platform ceiling never applies.
 */
export function planFootageUpload(file: { size: number }): FootageUploadPlan {
  return file.size > FOOTAGE_SINGLE_SHOT_BYTES ? { mode: 'multipart' } : { mode: 'single-shot' };
}

export interface VideoMetadata {
  filename: string;
  durationSeconds: number;
  width: number;
  height: number;
  resolution: string;
  codec: string;
  sizeBytes: number;
  playable: boolean;
}

export type FootageCheck = { ok: true } | { ok: false; reason: string };

export function checkFootageFile(file: { name: string; size: number; type?: string }): FootageCheck {
  if (!VIDEO_EXTENSIONS.test(file.name)) {
    return { ok: false, reason: 'Only MP4/H.264 footage is accepted (.mp4, .m4v, .mov).' };
  }
  if (file.size === 0) return { ok: false, reason: 'That file is empty.' };
  if (file.size > FOOTAGE_MAX_BYTES) {
    return {
      ok: false,
      reason: `${formatBytes(file.size)} is over the ${formatBytes(FOOTAGE_MAX_BYTES)} upload limit — split the match into smaller files.`,
    };
  }
  if (file.type && !file.type.startsWith('video/') && file.type !== 'application/octet-stream') {
    return { ok: false, reason: `That file is ${file.type}, not video.` };
  }
  return { ok: true };
}

/**
 * Reads duration/resolution from the file itself (the browser decodes the metadata), so the
 * player and the D1 row agree before anything is uploaded.
 */
export async function probeVideoMetadata(file: File): Promise<VideoMetadata> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.src = url;

  try {
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('Timed out reading video metadata')), 15_000);
      video.addEventListener(
        'loadedmetadata',
        () => {
          window.clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
      video.addEventListener(
        'error',
        () => {
          window.clearTimeout(timer);
          reject(new Error('This browser could not read that video — check it is H.264/MP4'));
        },
        { once: true },
      );
    });

    const canPlay = video.canPlayType('video/mp4; codecs="avc1.4d002a"');
    return {
      filename: file.name,
      durationSeconds: Number.isFinite(video.duration) ? video.duration : 0,
      width: video.videoWidth,
      height: video.videoHeight,
      resolution: video.videoWidth ? `${video.videoWidth}x${video.videoHeight}` : 'unknown',
      codec: canPlay ? 'avc1' : 'unknown',
      sizeBytes: file.size,
      playable: canPlay !== '',
    };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

/** 3725 → "1:02:05"; 65 → "1:05". */
export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm}:${String(s).padStart(2, '0')}`;
}
